import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { Colors } from '../../constants/theme';
import { createAudition } from '../../redux/audition/actions';
import { brandsSelector } from '../../redux/buzzbridge/selectors';
import { normalizeCastingDirectorRecord } from '../../utils/castingDirectorNormalize';
import { BrandCampaignStepper } from './BrandCampaignStepper';
import { BrandCampaignStepFooter } from './BrandCampaignStepFooter';
import { BrandAuditionBasicsStep, AuditionBasicsState } from './BrandAuditionBasicsStep';
import { BrandAuditionRolesStep, AuditionRole } from './BrandAuditionRolesStep';
import {
    autoFillAuditionRoles,
    autoFillAuditionStep0,
    autoFillAuditionStep1Basics,
    autoFillAuditionStep2,
    autoFillAuditionStep3,
} from './brandAuditionFormHelpers';
import { buildBrandAuditionCreatePayload } from './buildBrandAuditionCreatePayload';
import {
    AUDITION_WIZARD_INITIAL_BASICS,
    clearAuditionWizardDraft,
    loadAuditionWizardDraft,
    saveAuditionWizardDraft,
} from './auditionDraftStorage';
import { plainTextFromHtml } from './contestWizard/richTextUtils';
import { showToastMessage } from '../../utils/AppUtils';
import { hasActiveCampaignHostPlanSelector } from '../../redux/campaignHostSubscription/selectors';
import { fetchCampaignHostMine } from '../../redux/campaignHostSubscription/actions';
import { CampaignHostTierPickerModal } from './subscription/CampaignHostTierPickerModal';
import { CampaignHostCheckoutDialog } from './subscription/CampaignHostCheckoutDialog';
import { useCampaignHostCheckout } from './subscription/useCampaignHostCheckout';
import type { ICampaignHostPackage } from '../../types/global/campaignHostSubscription.types';

const AUDITION_WIZARD_STEPS = [
    'Project Setup',
    'Roles & Requirements',
    'Schedule & Location',
    'Media & Production',
] as const;

export interface AuditionCuratedFilterSnapshot {
    gig_type: string;
    shoot_location: string;
    /** Per-role gig types (Roles step); used when project `gig_type` is mixed to derive profile types for curated search. */
    role_gig_types: string[];
    min_age: string;
    max_age: string;
}

export interface BrandNewAuditionFormProps {
    themeColors: Colors;
    onCuratedFilterChange?: (snapshot: AuditionCuratedFilterSnapshot) => void;
    /** Thspian user ids marked Invited in Curated talents (sent after audition create). */
    curatedTalentInviteUserIds?: string[];
}

export function BrandNewAuditionForm({
    themeColors,
    onCuratedFilterChange,
    curatedTalentInviteUserIds = [],
}: BrandNewAuditionFormProps) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userBrands = useSelector(brandsSelector);
    const userCastingDirectors = userBrands
        .map(b => {
            const companyId: string | undefined =
                typeof b.company === 'string' ? b.company : (b.company as any)?._id ?? (b.company as any)?.id;
            if (!companyId) return null;
            return normalizeCastingDirectorRecord({ ...b, _id: companyId, company_name: b.name, profile_picture: b.logo ?? '' });
        })
        .filter(Boolean);
    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const [guardOpen, setGuardOpen] = useState(false);

    // Fetch subscription state fresh on mount so the guard has real data
    useEffect(() => {
        dispatch(fetchCampaignHostMine());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Once data is back: no plan → open guard; has plan → close it if somehow open
    useEffect(() => {
        if (!hasPlan) {
            setGuardOpen(true);
        } else {
            setGuardOpen(false);
        }
    }, [hasPlan]);

    const { checkoutOpen, setCheckoutOpen, beginMonthly, beginLtd, onPaymentMethodReady, checkoutTitle } =
        useCampaignHostCheckout({
            onSubscriptionComplete: () => setGuardOpen(false),
        });

    const handleGuardSelectMonthly = useCallback(
        (pkg: ICampaignHostPackage) => {
            setGuardOpen(false);
            beginMonthly(pkg);
        },
        [beginMonthly],
    );
    const handleGuardSelectLtd = useCallback(
        (pkg: ICampaignHostPackage) => {
            setGuardOpen(false);
            beginLtd(pkg);
        },
        [beginLtd],
    );

    const [step, setStep] = useState(0);
    const [basics, setBasics] = useState<AuditionBasicsState>(AUDITION_WIZARD_INITIAL_BASICS);
    const [roles, setRoles] = useState<AuditionRole[]>([]);
    const [draftHint, setDraftHint] = useState<string | null>(null);
    const [showValidation, setShowValidation] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [step]);

    useEffect(() => {
        const loaded = loadAuditionWizardDraft();
        if (loaded) {
            setBasics(loaded.basics);
            setRoles(loaded.roles);
            setStep(loaded.step);
            setDraftHint('Draft restored — re-upload cover image or PDF if you had attached files');
        }
    }, []);

    /** Serialize role gig types so curated snapshot does not re-fire when only other role fields change. */
    const roleGigTypesKey = useMemo(() => roles.map(r => (r.gig_type || '').trim()).join('\u0001'), [roles]);

    const curatedRoleGigTypes = useMemo(() => roleGigTypesKey.split('\u0001').filter(Boolean), [roleGigTypesKey]);

    useEffect(() => {
        onCuratedFilterChange?.({
            gig_type: basics.gig_type,
            shoot_location: basics.shoot_location.trim(),
            role_gig_types: curatedRoleGigTypes,
            min_age: basics.min_age.trim(),
            max_age: basics.max_age.trim(),
        });
    }, [
        basics.gig_type,
        basics.shoot_location,
        basics.min_age,
        basics.max_age,
        curatedRoleGigTypes,
        onCuratedFilterChange,
    ]);

    const updateBasics = useCallback((field: keyof AuditionBasicsState, value: any) => {
        setBasics(prev => ({ ...prev, [field]: value }));
    }, []);

    const canAdvanceProjectSetup = useMemo(
        () =>
            basics.title.trim().length > 0 &&
            plainTextFromHtml(basics.synopsis).length > 0 &&
            basics.audition_type.length > 0 &&
            basics.type_of_work.length > 0 &&
            basics.gig_type.length > 0,
        [basics],
    );

    const rolesBusinessRulesSatisfied = useMemo(
        () =>
            roles.length > 0 &&
            roles.every(r => {
                if (r.character.trim().length === 0) return false;
                if (!(r.gig_type || '').trim()) return false;
                if ((r.gig_type || '').trim().toLowerCase() === 'cast') {
                    const min = parseInt(String(r.min_age ?? '').trim(), 10);
                    const max = parseInt(String(r.max_age ?? '').trim(), 10);
                    if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
                    if (min < 1 || max > 120) return false;
                    if (min > max) return false;
                }
                return true;
            }),
        [roles],
    );

    const canAdvanceRoles = rolesBusinessRulesSatisfied;

    /** Schedule & Location step: require shoot location + casting deadline + job start date before continuing. */
    const canAdvanceScheduleLocation = useMemo(
        () =>
            basics.shoot_location.trim().length > 0 &&
            basics.casting_expiration.trim().length > 0 &&
            basics.job_start_date.trim().length > 0,
        [basics.shoot_location, basics.casting_expiration, basics.job_start_date],
    );

    const totalRoles = roles.length;
    const totalBudget = roles.reduce((sum, r) => sum + Number(r.compensation || 0), 0);

    const hasCover = useMemo(
        () =>
            Boolean(
                (Array.isArray(basics.imageCover) && basics.imageCover.length > 0) || basics.imageCover === 'no-image',
            ),
        [basics.imageCover],
    );

    const canSubmitAudition = useMemo(() => {
        const hasRequiredBasics =
            basics.title.trim().length > 0 &&
            plainTextFromHtml(basics.synopsis).length > 0 &&
            basics.audition_type.trim().length > 0 &&
            basics.type_of_work.trim().length > 0 &&
            basics.gig_type.trim().length > 0 &&
            basics.shoot_location.trim().length > 0 &&
            basics.casting_expiration.trim().length > 0 &&
            basics.job_start_date.trim().length > 0;

        const hasRequiredRoles = rolesBusinessRulesSatisfied;
        const hasPublishScheduleIfNeeded =
            basics.publishStatus !== 'schedule' || Boolean(basics.scheduled_published_date?.trim());

        const hasProductionCompany = Boolean(basics.production_company && basics.production_company._id);

        return hasRequiredBasics && hasRequiredRoles && hasCover && hasPublishScheduleIfNeeded && hasProductionCompany;
    }, [basics, hasCover, rolesBusinessRulesSatisfied]);

    const persistDraft = useCallback(
        (nextStep: number) => {
            saveAuditionWizardDraft(nextStep, basics, roles);
        },
        [basics, roles],
    );

    const handleSaveDraft = useCallback(() => {
        persistDraft(step);
        const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        setDraftHint(`Saved at ${time}`);
        showToastMessage('Audition draft saved', 'success');
    }, [persistDraft, step]);

    const handleNext = useCallback(() => {
        if (step < AUDITION_WIZARD_STEPS.length - 1) {
            const nextStep = step + 1;
            persistDraft(nextStep);
            setShowValidation(false);
            setStep(nextStep);
        } else {
            const payload = buildBrandAuditionCreatePayload(basics, roles);
            clearAuditionWizardDraft();
            dispatch(
                createAudition({
                    ...payload,
                    brandCuratedInviteUserIds: curatedTalentInviteUserIds,
                } as never),
            );
            navigate('/brand');
        }
    }, [step, basics, roles, dispatch, navigate, curatedTalentInviteUserIds, persistDraft]);

    const handleBack = useCallback(() => {
        if (step > 0) {
            setShowValidation(false);
            setStep(s => s - 1);
        }
    }, [step]);

    const disableNext =
        step === 0
            ? !canAdvanceProjectSetup
            : step === 1
            ? !canAdvanceRoles
            : step === 2
            ? !canAdvanceScheduleLocation
            : step === 3
            ? !canSubmitAudition
            : false;

    const handleAutoFill = useCallback(() => {
        if (step === 0) {
            setBasics(prev => ({ ...prev, ...autoFillAuditionStep0() }));
        } else if (step === 1) {
            setBasics(prev => ({ ...prev, ...autoFillAuditionStep1Basics(prev.gig_type) }));
            setRoles(autoFillAuditionRoles());
        } else if (step === 2) {
            setBasics(prev => ({ ...prev, ...autoFillAuditionStep2() }));
        } else {
            setBasics(prev => ({
                ...prev,
                ...autoFillAuditionStep3(),
                production_company: userCastingDirectors[0] ?? prev.production_company,
            }));
        }
    }, [step, userCastingDirectors]);

    return (
        <>
            <CampaignHostTierPickerModal
                open={guardOpen}
                onClose={() => {}}
                onSelectMonthly={handleGuardSelectMonthly}
                onSelectLtd={handleGuardSelectLtd}
            />

            <CampaignHostCheckoutDialog
                open={checkoutOpen}
                title={checkoutTitle}
                onClose={() => setCheckoutOpen(false)}
                onPaymentMethodReady={onPaymentMethodReady}
            />

            <BrandCampaignStepper
                currentStep={step}
                totalSteps={AUDITION_WIZARD_STEPS.length}
                stepLabel={AUDITION_WIZARD_STEPS[step]}
                stepSubtitle={draftHint && step === 0 ? draftHint : undefined}
                onAutoFill={handleAutoFill}
            />

            {step === 0 && (
                <BrandAuditionBasicsStep
                    themeColors={themeColors}
                    state={basics}
                    onChange={updateBasics}
                    wizardPage={0}
                    showValidation={showValidation}
                />
            )}
            {step === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <BrandAuditionRolesStep themeColors={themeColors} roles={roles} onRolesChange={setRoles} />
                    <BrandAuditionBasicsStep
                        themeColors={themeColors}
                        state={basics}
                        onChange={updateBasics}
                        wizardPage={1}
                        showValidation={showValidation}
                    />
                </Box>
            )}
            {step === 2 && (
                <BrandAuditionBasicsStep
                    themeColors={themeColors}
                    state={basics}
                    onChange={updateBasics}
                    wizardPage={2}
                    showValidation={showValidation}
                />
            )}
            {step === 3 && (
                <BrandAuditionBasicsStep
                    themeColors={themeColors}
                    state={basics}
                    onChange={updateBasics}
                    wizardPage={3}
                    showValidation={showValidation}
                />
            )}

            <BrandCampaignStepFooter
                onBack={step > 0 ? handleBack : undefined}
                onNext={handleNext}
                onNextAttempt={() => setShowValidation(true)}
                onSaveDraft={handleSaveDraft}
                draftSavedHint={draftHint && !draftHint.startsWith('Draft restored') ? draftHint : null}
                nextLabel={
                    step === AUDITION_WIZARD_STEPS.length - 1 ? 'Submit Audition' : `Continue to Step ${step + 2}`
                }
                disableNext={disableNext}
                summaryStats={
                    step === 1 && roles.length > 0
                        ? [{ label: `${totalRoles} Total Role${totalRoles !== 1 ? 's' : ''}` }]
                        : undefined
                }
                summaryBudgetLabel={step === 1 ? 'Total Budget' : undefined}
                summaryBudgetValue={step === 1 && totalBudget > 0 ? `$${totalBudget.toLocaleString()}.00` : undefined}
            />
        </>
    );
}
